
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface DashboardStats {
  clientsCount: number;
  overdueTasks: number;
  onTimeTasks: number;
  agencyStatus: 'on-time' | 'overdue' | 'mixed';
}

export function useDashboardStats() {
  const [stats, setStats] = useState<DashboardStats>({
    clientsCount: 0,
    overdueTasks: 0,
    onTimeTasks: 0,
    agencyStatus: 'on-time'
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        setIsLoading(true);

        // Get session
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) return;

        // Count clients
        const { data: clientsData, error: clientsError } = await supabase
          .from('clients')
          .select('id')
          .eq('user_id', sessionData.session.user.id)
          .eq('status', 'active');
        
        if (clientsError) throw clientsError;

        // Get user's boards to analyze tasks
        const { data: boardsData } = await supabase
          .from('kanban_boards')
          .select('id')
          .eq('user_id', sessionData.session.user.id)
          .eq('is_archived', false);

        let overdueTasks = 0;
        let onTimeTasks = 0;

        if (boardsData && boardsData.length > 0) {
          const boardIds = boardsData.map(board => board.id);

          // Get columns for these boards
          const { data: columnsData } = await supabase
            .from('kanban_columns')
            .select('id')
            .in('board_id', boardIds);

          if (columnsData && columnsData.length > 0) {
            const columnIds = columnsData.map(col => col.id);

            // Get all cards with due dates
            const { data: cardsData } = await supabase
              .from('kanban_cards')
              .select('id, due_date, is_completed')
              .in('column_id', columnIds)
              .eq('is_archived', false)
              .not('due_date', 'is', null);

            if (cardsData) {
              const today = new Date();
              today.setHours(23, 59, 59, 999); // End of today

              cardsData.forEach(card => {
                if (card.due_date && !card.is_completed) {
                  const dueDate = new Date(card.due_date);
                  if (dueDate < today) {
                    overdueTasks++;
                  } else {
                    onTimeTasks++;
                  }
                }
              });
            }
          }
        }

        // Determine agency status
        let agencyStatus: 'on-time' | 'overdue' | 'mixed' = 'on-time';
        if (overdueTasks > 0 && onTimeTasks > 0) {
          agencyStatus = 'mixed';
        } else if (overdueTasks > 0) {
          agencyStatus = 'overdue';
        }

        setStats({
          clientsCount: clientsData?.length || 0,
          overdueTasks,
          onTimeTasks,
          agencyStatus
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchStats();
  }, []);

  return { stats, isLoading };
}
